import { FC, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge, Button, Dialog } from '@robocorp/components';
import { IconPlus, IconRefresh3 } from '@robocorp/icons/iconic';
import { CurrentWorkspaceRestriction } from '~/components';
import { FilterSettings, useDataExplorer, useLocalStorage } from '~/hooks';
import {
  defaultWorkItemsData,
  useCurrentWorkspace,
  useProcessQuery,
  useWorkItemsLazyQuery,
} from '~/queries';
import CreateWorkItem from './workItem/CreateWorkItem';
import { WorkDataTable } from './components/table/WorkDataTable';

type Props = {
  createItemOpen: boolean;
  setCreateItemOpen: (state: boolean) => void;
  pageSize?: FilterSettings['size'];
};

const WorkData: FC<Props> = ({ createItemOpen, setCreateItemOpen, pageSize = 25 }) => {
  const { t } = useTranslation();

  const [, config] = useDataExplorer();
  const scope: 'process' | 'processRun' = config?.scope;
  const processId = config?.pId;
  const processRunId = config?.pRunId || '';

  const {
    storageValue: { size },
  } = useLocalStorage({ key: 'process_workData', initialValue: { size: pageSize } });

  const { data: currentWorkspace } = useCurrentWorkspace();
  const { data: process } = useProcessQuery(processId);

  const [{ data: { total } = defaultWorkItemsData, refetch: refetchWorkItems }, getWorkItems] =
    useWorkItemsLazyQuery();

  useEffect(() => {
    const args = {
      processId,
      filters: {},
      queryArgs: {
        size,
        cursor: null,
        sortBy: 'createTs',
        sortOrder: 'desc' as const,
        exceptionType: undefined,
        search: '',
      },
    };

    if (scope === 'process') {
      getWorkItems({ ...args, scope });
    }

    if (scope === 'processRun') {
      getWorkItems({ ...args, processRunId, scope });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [processId, processRunId, scope, size]);

  const onToggleCreateItemDialog = useCallback(
    () => setCreateItemOpen(!createItemOpen),
    [createItemOpen, setCreateItemOpen],
  );

  const description = useMemo(
    () =>
      [process?.name, currentWorkspace?.org_name, currentWorkspace?.name]
        .filter(Boolean)
        .join(' · '),
    [currentWorkspace?.name, currentWorkspace?.org_name, process?.name],
  );

  return (
    <>
      <Dialog.Header>
        <Dialog.Header.Title title={t('workItems')}>
          <Badge label={total ?? 0} />
        </Dialog.Header.Title>
        <Dialog.Header.Actions>
          <CurrentWorkspaceRestriction permission="trigger_processes">
            <Button variant="ghost" icon={IconPlus} onClick={onToggleCreateItemDialog}>
              {t('createWorkItem')}
            </Button>
          </CurrentWorkspaceRestriction>
          <CurrentWorkspaceRestriction permission="read_work_item_data">
            <Button variant="ghost" icon={IconRefresh3} onClick={() => refetchWorkItems()}>
              {t('refresh')}
            </Button>
          </CurrentWorkspaceRestriction>
        </Dialog.Header.Actions>
        <Dialog.Header.Description>{description}</Dialog.Header.Description>
      </Dialog.Header>
      <WorkDataTable processId={processId} total={total} pageSize={pageSize} />
      <CreateWorkItem
        processId={processId}
        open={createItemOpen}
        onClose={onToggleCreateItemDialog}
      />
    </>
  );
};

export default WorkData;
